import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError, auth } from "../lib/auth";
import { toast } from "sonner";
import { LogIn, AtSign, KeyRound, Lock, MessageSquare } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refreshAccount } = useAuth();
  const [ident, setIdent] = useState("");
  const [pw, setPw] = useState("");
  const [pin, setPin] = useState("");
  const [sms, setSms] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { email: ident, password: pw };
      if (pin) payload.pin = pin;
      if (sms) payload.sms_code = sms;
      const { data } = await auth.post("/auth/login", payload);
      // Owner branch: server set the owner cookie. Send to /owner.
      if (data?.role === "owner") {
        toast.success("OWNER_AUTHED");
        nav("/owner", { replace: true });
        return;
      }
      const me = await refreshAccount();
      toast.success(`WELCOME · ${me?.name || me?.email}`);
      const dest = loc.state?.from || "/dashboard";
      nav(dest, { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail?.error === "sms_required" || detail?.error === "owner_sms_required") {
        setShowSms(true);
        const masked = detail?.phone_masked ? ` (sent to ${detail.phone_masked})` : "";
        setErr(`SMS code required${masked}. Enter the 6-digit code we just texted you.`);
      } else if (detail?.error === "sms_invalid" || detail?.error === "owner_sms_invalid") {
        setErr("Invalid SMS code. Please try again.");
      } else {
        setErr(formatApiError(e2));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <div className="flex-1 hidden lg:flex flex-col justify-center px-12 border-r border-[#27272A] grid-bg">
        <Link to="/" className="flex items-center gap-2 font-mono mb-6">
          <span className="inline-block w-2 h-2 bg-amber-400 animate-pulse" />
          <span className="text-base font-extrabold tracking-tight text-white">THE_MINDSTEIN</span>
        </Link>
        <h1 className="font-mono text-4xl tracking-tight">
          Welcome back to the <span className="text-amber-400">thinking machine</span>.
        </h1>
        <p className="text-zinc-400 mt-4 max-w-md leading-relaxed">
          7-day free trial unlocks every paid feature plus 5 free MindStein usages.
          After that, pick a plan to keep going.
        </p>
        <ol className="mt-8 space-y-2 text-[12px] font-mono text-zinc-500">
          <li>· 10 topic libraries · 10 fresh ideas / library / day</li>
          <li>· Innovation Scout · up to 50 frontier signals / day · 500 live cap · 30-day retention</li>
          <li>· Fun Facts · 10 oddly-true facts / day</li>
          <li>· MindStein Bot — combine / compare / expand</li>
          <li>· Personal Save Library — keep ideas forever</li>
        </ol>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm border border-[#27272A] bg-[#0E0E0E] p-6 space-y-4"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            SIGN_IN
          </div>
          <h2 className="font-mono text-2xl tracking-tight">Log in</h2>

          <div
            data-testid="google-signin-coming-soon"
            className="w-full h-10 flex items-center justify-center gap-2 border border-dashed border-[#27272A] bg-[#0A0A0A] text-[11px] font-mono uppercase tracking-wider text-zinc-500 cursor-not-allowed select-none"
            aria-disabled="true"
            title="Google sign-in is coming soon — please use email + password for now."
          >
            GOOGLE_SIGN-IN · COMING_SOON
          </div>

          <div className="flex items-center gap-2 text-[10px] font-mono uppercase text-zinc-600">
            <div className="flex-1 h-px bg-[#27272A]" />
            EMAIL_+_PASSWORD
            <div className="flex-1 h-px bg-[#27272A]" />
          </div>

          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">EMAIL_OR_USERNAME</div>
            <div className="flex items-center border border-[#27272A] mt-1 focus-within:border-amber-400 bg-[#0A0A0A]">
              <AtSign size={12} className="ml-2 text-zinc-500" />
              <input
                data-testid="login-email"
                required
                value={ident}
                onChange={(e) => setIdent(e.target.value)}
                className="flex-1 h-9 px-2 bg-transparent outline-none text-[12px] font-mono"
              />
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">PASSWORD</div>
            <div className="flex items-center border border-[#27272A] mt-1 focus-within:border-amber-400 bg-[#0A0A0A]">
              <KeyRound size={12} className="ml-2 text-zinc-500" />
              <input
                data-testid="login-password"
                type="password"
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="flex-1 h-9 px-2 bg-transparent outline-none text-[12px] font-mono"
              />
            </div>
          </label>

          {showPin || ident.toLowerCase() === "owner" ? (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">PIN (OPTIONAL)</div>
              <div className="flex items-center border border-[#27272A] mt-1 focus-within:border-amber-400 bg-[#0A0A0A]">
                <Lock size={12} className="ml-2 text-zinc-500" />
                <input
                  data-testid="login-pin"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  autoComplete="off"
                  className="flex-1 h-9 px-2 bg-transparent outline-none text-[12px] font-mono"
                />
              </div>
            </label>
          ) : (
            <button
              type="button"
              data-testid="toggle-pin"
              onClick={() => setShowPin(true)}
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-white"
            >
              + ADD_PIN_(OPTIONAL)
            </button>
          )}

          {showSms && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">SMS_CODE (2FA)</div>
              <div className="flex items-center border border-[#27272A] mt-1 focus-within:border-amber-400 bg-[#0A0A0A]">
                <MessageSquare size={12} className="ml-2 text-zinc-500" />
                <input
                  data-testid="login-sms"
                  value={sms}
                  onChange={(e) => setSms(e.target.value)}
                  placeholder="6-digit code"
                  className="flex-1 h-9 px-2 bg-transparent outline-none text-[12px] font-mono"
                />
              </div>
              <p className="text-[10px] text-amber-300 mt-1 font-mono">
                MOCKED · enter any 4-6 digits for now (real SMS coming)
              </p>
              <div
                data-testid="login-authenticator-coming-soon"
                className="mt-2 border border-dashed border-[#27272A] bg-[#0A0A0A] p-2 flex items-center gap-2"
                aria-disabled="true"
                title="Google / Microsoft Authenticator support is on the way."
              >
                <KeyRound size={11} className="text-zinc-500" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 leading-snug">
                  ALT · AUTHENTICATOR (GOOGLE / MICROSOFT) · COMING SOON
                  <div className="normal-case tracking-normal text-[10.5px] text-zinc-500 font-sans">
                    Switch to 6-digit TOTP codes from Google Authenticator or Microsoft Authenticator on your next sign-in.
                  </div>
                </div>
              </div>
            </label>
          )}

          <div
            data-testid="login-lockout-hint"
            className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 leading-snug"
          >
            10 failed attempts locks the account. Reset via email at any time
            using the link below.
          </div>

          {err && (
            <div data-testid="login-error" className="border border-rose-500/40 bg-rose-500/10 text-rose-200 text-[11px] font-mono p-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            className="w-full h-10 bg-white text-black hover:bg-amber-300 font-mono uppercase text-[12px] tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <LogIn size={12} />
            {busy ? "SIGNING_IN…" : "SIGN_IN"}
          </button>

          <div className="text-[11px] text-zinc-500 font-mono">
            New here?{" "}
            <Link to="/register" className="text-amber-300 hover:underline" data-testid="goto-register">
              Start the 7-day free trial →
            </Link>
          </div>

          {/* Partner demo — PIN-gated auto-fill (Phase 30) */}
          <PartnerDemoBlock
            onFill={() => {
              setIdent("partner");
              setPw("partner01!");
              setPin("");
              setShowPin(false);
            }}
          />
        </form>
      </div>
    </div>
  );
}

/**
 * PartnerDemoBlock — Phase 30. Requires the demo PIN (`89897`) before the
 * one-click autofill is exposed. Clients you share the PIN with can
 * unlock instantly; anonymous strangers can't. Once unlocked the block
 * stays open for the rest of the session (component lifetime).
 */
function PartnerDemoBlock({ onFill }) {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [err, setErr] = useState("");
  const DEMO_PIN = "89897";

  const tryUnlock = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (pin === DEMO_PIN) {
      setUnlocked(true);
      setErr("");
      toast.success("PARTNER_DEMO_UNLOCKED · click AUTO-FILL to preview");
    } else {
      setErr("Incorrect PIN — ask the MindStein team for the demo code.");
    }
  };

  return (
    <div
      data-testid="login-partner-demo"
      className="mt-3 border border-purple-500/40 bg-purple-500/5 p-3 text-[11px] font-mono space-y-2"
    >
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.2em] text-purple-300">
          PARTNER DEMO · PIN_GATED · 15 USAGES · RESETS ON LOGIN/LOGOUT
        </span>
      </div>

      {!unlocked ? (
        <div className="space-y-2">
          <div className="text-[10px] text-zinc-400">
            Enter the 5-digit demo PIN to reveal the auto-fill button.
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="partner-demo-pin"
              type="password"
              inputMode="numeric"
              maxLength={5}
              autoComplete="off"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ""));
                setErr("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  tryUnlock();
                }
              }}
              placeholder="•••••"
              className="flex-1 h-9 px-2 bg-[#0A0A0A] border border-purple-500/30 focus:border-purple-400 outline-none text-[13px] font-mono tracking-widest text-center text-white"
            />
            <button
              type="button"
              data-testid="partner-demo-unlock"
              onClick={tryUnlock}
              disabled={pin.length !== 5}
              className="h-9 px-3 border border-purple-400 text-purple-200 hover:bg-purple-400 hover:text-black text-[10px] uppercase tracking-wider disabled:opacity-40"
            >
              UNLOCK
            </button>
          </div>
          {err && (
            <div className="text-[10px] text-rose-300" data-testid="partner-demo-err">
              {err}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            data-testid="login-fill-partner"
            onClick={onFill}
            className="w-full h-9 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 text-[10px] uppercase tracking-wider font-extrabold"
          >
            AUTO-FILL PARTNER CREDENTIALS
          </button>
          <div className="text-zinc-400">
            partner · <span className="text-purple-300">partner01!</span> ·{" "}
            <span className="text-zinc-500">no PIN required at sign-in</span>
          </div>
        </div>
      )}
    </div>
  );
}
